// frontend/src/pages/ServiceReport/SummaryServiceReport.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, Typography, Row, Col, Button, Table, Spin, Tag, Space, 
  Modal, Form, Select, DatePicker, message, Popconfirm, Empty,
  Tooltip, Badge
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, 
  FileExcelOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import api from '../../utils/api';
import '../Analytics/Analytics.css';

const { Title, Text } = Typography;
const { Option } = Select;

const SummaryServiceReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Fetch reports on load
  useEffect(() => {
    fetchReports();
    fetchContracts();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/reports/summary-service-report');
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to load reports',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get('/api/contracts');
        console.log('=== CONTRACTS API RESPONSE ===');
        console.log('Full response:', response.data);
        console.log('Contracts array:', response.data.data);
      if (response.data.success) {
        setContracts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const handleCreateReport = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.post('/api/reports/summary-service-report', {
        contractName: values.contractName,
        reportMonth: values.reportMonth.format('YYYY-MM'),
        appsOrInfra: values.appsOrInfra
      });
      
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Report created successfully',
        });
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchReports();
      }
    } catch (error) {
      console.error('Error creating report:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to create report',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReport = async (id) => {
    try {
      const response = await api.delete(`/api/reports/summary-service-report/${id}`);
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Report deleted successfully',
        });
        fetchReports();
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete report',
      });
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'draft':
        return <Tag icon={<EditOutlined />} color="default">Draft</Tag>;
      case 'pending_review':
        return <Tag icon={<ClockCircleOutlined />} color="processing">Pending Review</Tag>;
      case 'pending_approval':
        return <Tag icon={<ClockCircleOutlined />} color="warning">Pending Approval</Tag>;
      case 'approved':
        return <Tag icon={<CheckCircleOutlined />} color="success">Approved</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getActionButtons = (record) => {
    const canEdit = record.status === 'draft';
    const canView = true;
    const canDelete = record.status === 'draft';
    
    return (
      <Space>
        <Tooltip title="View/Edit">
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => navigate(`/reports/summary-service-report/${record.id}`)}
          />
        </Tooltip>
        {canDelete && (
          <Popconfirm
            title="Delete Report"
            description="Are you sure you want to delete this report? This action cannot be undone."
            onConfirm={() => handleDeleteReport(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        )}
      </Space>
    );
  };

  const columns = [
    {
        title: 'Report Name',
        dataIndex: 'report_uuid',
        key: 'report_uuid',
        width: 350,
        render: (text) => (
            <Tooltip title={text} placement="topLeft">
                <div style={{ 
                    fontSize: '13px', 
                    fontWeight: 500, 
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.4'
                }}>
                    {text}
                </div>
            </Tooltip>
        )
    },
    {
      title: 'Contract',
      dataIndex: 'contract_name',
      key: 'contract_name',
      width: 200,
    },
    {
      title: 'Month',
      dataIndex: 'report_month',
      key: 'report_month',
      width: 120,
      render: (date) => dayjs(date).format('MMMM YYYY')
    },
    {
      title: 'Apps/Infra',
      dataIndex: 'apps_or_infra',
      key: 'apps_or_infra',
      width: 120,
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Prepared By',
      dataIndex: 'prepared_by_name',
      key: 'prepared_by_name',
      width: 150,
      render: (text) => text || '-'
    },
    {
      title: 'Prepared Date',
      dataIndex: 'prepared_signed_at',
      key: 'prepared_signed_at',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('DD/MM/YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => getActionButtons(record)
    }
  ];

  // Disable future months
  const disabledDate = (current) => {
    return current && current > dayjs().endOf('month');
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <Title level={2} className="analytics-title">Summary Service Report</Title>
        <Text className="analytics-description">
          Create and manage monthly service reports for contract compliance. Fill daily activities and submit for signature workflow.
        </Text>
      </div>

      <Card className="filter-card">
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <Space>
              <Tag icon={<FileExcelOutlined />} color="purple">Interactive Report</Tag>
              <Text type="secondary">Create, edit, and submit for signatures</Text>
            </Space>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setCreateModalVisible(true)}
            >
              Create New Report
            </Button>
          </Col>
        </Row>
      </Card>

      <Card className="report-card">
        <Table
          columns={columns}
          dataSource={reports}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} reports`
          }}
          scroll={{ x: 1300 }}
          className="report-table"
        />
        
        {!loading && reports.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Empty
              description="No reports found. Click 'Create New Report' to get started."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </Card>

      {/* Create Report Modal */}
      <Modal
        title="Create New Summary Service Report"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateReport}
        >
          <Form.Item
            name="contractName"
            label="Contract"
            rules={[{ required: true, message: 'Please select a contract' }]}
          >
            <Select placeholder="Select contract" showSearch optionFilterProp="children">
              {contracts.map(contract => (
                <Option key={contract.id} value={contract.contractName}>
                  {contract.contractName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reportMonth"
            label="Report Month"
            rules={[{ required: true, message: 'Please select month' }]}
          >
            <DatePicker 
              picker="month" 
              style={{ width: '100%' }}
              format="MMMM YYYY"
              disabledDate={disabledDate}
              placeholder="Select month"
            />
          </Form.Item>

          <Form.Item
            name="appsOrInfra"
            label="Apps / Infra"
            initialValue={user.userType === 'partner' ? 'Apps' : 'Infra'}
            tooltip={user.userType === 'partner' ? 'Default: Apps (can be changed)' : 'Default: Infra (can be changed)'}
          >
            <Select placeholder="Select type">
              <Option value="Apps">Apps</Option>
              <Option value="Infra">Infra</Option>
              <Option value="Apps & Infra">Apps & Infra</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create Report
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SummaryServiceReport;